"use client";

import { useEffect, useMemo, useState } from "react";
import { EnrichedProgram, ProcessedCourse } from "@/common/course/types";
import { Program } from "@/common/program/types";
import { ProductViewTracker } from "@/components/ProductViewTracker";
import { useCourseCheckoutUrl } from "@/hooks/useCourseCheckoutUrl";
import { useCompareCtaUrl } from "@/components/ProductCompare/useCompareCtaUrl";
import isCourseArchived from "@/lib/course/utils/isCourseArchived";
import useClientEnrollments from "@/lib/partner-api/useClientEnrollments";
import useClientEntitlements from "@/lib/partner-api/useClientEntitlements";
import useUserEnrollments from "@/components/CourseContent/CourseContext/hooks/useUserEnrollments";
import useUserEntitlements from "@/components/CourseContent/CourseContext/hooks/useUserEntitlements";
import isUserEnrolled from "@/lib/course/utils/isUserEnrolled";
import ISO6391 from "iso-639-1";
import useLeadWithPaid from "@/monarch/rules/leadWithPaid/useLeadWithPaid";
import { useSingleCourseTrafficConfig } from "@/monarch/rules/singleCourseTraffic/useSingleCourseTraffic";
import { getAiTranslationLanguageLabels } from "@/lib/course/utils/getAiLanguages";
import CourseAbout from "@/components/CourseContent/ContentOverview";
import useCourseDetailsData from "@/components/CourseContent/CourseContext/hooks/useCourseDetailsData";
import CourseTaggedHeader from "../components/CourseTaggedHeader";
import OCPromoBanner from "../../ContentRefresh/OCPromoBanner";
import WhatYoullLearn from "../../ContentRefresh/WhatYoullLearn";
import { useCourseDetailPricingData } from "@/lib/checkout-geo-pricing";
import Curriculum from "../../Curriculum";
import CourseAwards from "@/components/CourseContent/templates/components/Awards";
import MidPageCTA from "./components/MidPageCTA";
import LearnerTestimonials from "../../LearnerTestimonials";
import InstructorList from "../../ContentRefresh/InstructorList";
import FAQs from "../../ContentRefresh/FAQs";
import CertificateWaysToTakeCourse from "@/components/Certificate/CertificateWaysToTakeCourse";
import CourseEnrollButton from "../../ContentRefresh/CourseEnroll/CourseEnrollButtons";
import EnrollLink from "../../ContentRefresh/CourseEnroll/CourseEnrollButtons/EnrollLink";
import AuditLink from "../../ContentRefresh/CourseEnroll/CourseEnrollButtons/AuditLink";
import WaysToTakeCourse from "../../ContentRefresh/WaysToTakeCourse";
import MoreAboutCourse from "../../ContentRefresh/MoreAboutCourse";
import SocialShare from "@/components/SocialShare";
import EnterpriseCTA from "../../ContentRefresh/EnterpriseCTA";
import OFACMessage from "@/components/CourseContent/ContentRefresh/OFACmessage";
import PartnerProgramRecommendations from "@/components/ParterProgramRecommendations";
import {
  mainContent as mainContentClass,
  maxWidthContainer,
} from "@/components/CourseContent/styles/constants";
import CertificateEmphasisTabs from "@/components/Certificate/CertificateEmphasisTabs";
import CertificateEmphasis from "@/components/Certificate/CertificateEmpashis";
import { RedesignedTrackComparison } from "@/components/Certificate/constants/enums";
import RelatedProgram from "../common/utils/renderRelatedProgram";
import AccordionText from "@/components/AccordionTextRefresh";
import AccordionTextItem from "@/components/AccordionTextRefresh/AccordionTextItemRefresh";
import getSortedComponentsBySectionOrder from "@/utils/getSortedComponentsBySectionOrder";
import SkillLanguageOverview from "../../ContentRefresh/SkillLanguageOverview";
import StepComponent from "@/components/StepComponent";
import StickyCTA from "../../ContentRefresh/StickyCTA";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import { CourseCurrencyBarSurface } from "@/components/CourseContent/templates/components/CourseCurrencySurfaces";
import CountdownBanner from "@/components/CountdownBanner";
import { getPartnersDisplayNames } from "@/lib/partner/utils/getPartnerDisplayName";
import BenefitsModule from "@/components/BenefitsModule";
import PartnerInstructors from "@/components/PartnerInstructors";
import BundleSavingsBanner from "@/components/BundleSavingsBanner/BundleSavingsBanner";
import { getProgramSearchUrl } from "../common/utils/getProgramSearchUrl";
import {
  CAREER_CARDS_VARIANTS,
  getIndustryKey,
  resolveCareerCardSkills,
} from "@/components/CareerCards/constants";
import CareerCards from "@/components/CareerCards";
import { courseDetailPageEventConfig } from "../common/const/const";
import SocialProof from "@/components/SocialProof";
import { headerStylesInDetailPages } from "@/components/SocialProof/const";
import ProductCompare from "@/components/ProductCompare";
import useCompareFallbackUuids from "@/components/ProductCompare/useCompareFallbackUuids";

type Props = {
  course: ProcessedCourse;
  program?: Program | null;
  programPacingType?: string;
  programDuration?: string;
  hoursPerWeekFormatted?: string;
  paymentOption: "free" | "upgradable" | "paid" | null;
  showCourseModes: boolean;
  category?: string;
  courseSlug?: string;
  enrichedPrograms?: EnrichedProgram[] | null;
  enrichedInstructors?: Instructor[] | null;
};

import type { Instructor } from "@/common/instructor/types";
import useTranslations from "@/i18n/useTranslations";

export default function DecidedUserCourseContent({
  course,
  showCourseModes,
  paymentOption,
  category,
  courseSlug,
  enrichedPrograms,
  enrichedInstructors,
}: Props) {
  const tCourse = useTranslations("Course");
  const tProgram = useTranslations("Program");
  //fetch enrollment data
  const { enrollments: userEnrollments } = useClientEnrollments();

  //fetch entitlement data
  const { entitlements: userEntitlements } = useClientEntitlements();

  const { setIsUserEnrolled, setUserEnrollments } = useUserEnrollments();
  const { setUserEntitled } = useUserEntitlements();

  const { setCourseDetailsData } = useCourseDetailsData();

  const pricingData = useCourseDetailPricingData({ course });
  const coursePrice =
    pricingData?.discountedPrice ?? pricingData?.originalPrice ?? null;

  // Generate checkout URL with SKU and course run key
  const checkoutUrl = useCourseCheckoutUrl(course);
  const compareCtaUrl = useCompareCtaUrl(course);

  const leadWithPaidData = useLeadWithPaid(course);

  const {
    headerBackground,
    isLoading,
    factoidsBar,
    hidePrice,
    pathInfo,
    emphasisModuleVersion,
    showRedesignedTrackComparison,
    information,
    stats,
    testimonial,
    tableDescription,
    tabsOrder,
    relatedPrograms,
    promoBanner,
    mainContent,
    collapsableContent,
    collapsableContentLabel,
    relatedProgramsRedesign: {
      design2: expandableSection,
      designToDisplay,
      cardType,
    } = {},
    carouselData,
    stepComponent,
    countdownBanner,
    benefitsModule,
    testimonialsModule,
    bundleSavingsBanner,
    showDynamicBadges,
    careerCards,
    productCompare,
  } = useSingleCourseTrafficConfig({ course });

  const compareFallbackUuids = useCompareFallbackUuids({
    source: productCompare?.relatedProductsSource,
    relatedProductsFilters: productCompare?.relatedProductsFilters,
    enrichedPrograms,
    course,
  });

  useEffect(() => {
    setCourseDetailsData({
      category: category,
      courseSlug: courseSlug,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, courseSlug]);

  useEffect(() => {
    if (userEntitlements && userEntitlements.length > 0) {
      const found =
        userEntitlements?.some(
          ({ courseUuid: userEntitledCourseUuid }) =>
            userEntitledCourseUuid === course.activeCourseRun?.uuid,
        ) || false;
      setUserEntitled(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEntitlements]);

  //set context enrollmment status
  useEffect(() => {
    if (userEnrollments && userEnrollments.length > 0) {
      setUserEnrollments(userEnrollments);
      const courseIds = course.activeCourseRuns?.map(({ key }) => key) || [];
      const isEnrolled = isUserEnrolled(userEnrollments, courseIds);
      setIsUserEnrolled(isEnrolled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEnrollments]);

  const { syllabusHtml, productFullDescription, video, productImageUrl } =
    course;

  const isCurriculumInMainContent = mainContent?.some(
    (component) => component.component === "curriculum",
  );

  const {
    weeksToComplete,
    minEffort,
    maxEffort,
    languageId,
    transcriptLanguages,
  } = course.activeCourseRun || {};
  const courseHeaderVideo = {
    image: {
      src: productImageUrl,
    },
    src: video.src,
  };
  const courseTitle = `${course.partners.map((obj) => obj.key).join(", ")}: ${course.productName}`;
  const titlePartner = getPartnersDisplayNames(course.partners);
  const isArchived = course.activeCourseRun
    ? isCourseArchived(course.activeCourseRun)
    : true;
  const formattedLanguage = languageId
    ? ISO6391.getNativeName(languageId.slice(0, 2))
    : undefined;
  const formattedTranscripts =
    transcriptLanguages?.map((transcript) =>
      ISO6391.getNativeName(transcript.slice(0, 2)),
    ) || [];

  const formattedAiLanguages = course.activeCourseRun
    ? getAiTranslationLanguageLabels(course.activeCourseRun)
    : [];

  const industryKey = useMemo(
    () =>
      getIndustryKey({
        title: course.productName,
        subjectEn: course.subjectEn || "",
        subjectEs: course.subjectEs || "",
        overview: course.productOverview,
        subjects: course.subjects,
        skills: course.skills,
      }),
    [
      course.productName,
      course.subjectEn,
      course.subjectEs,
      course.productOverview,
      course.subjects,
      course.skills,
    ],
  );

  const componentList = [
    {
      key: "promoBanner",
      renderer: () => {
        return promoBanner && course.activeCourseRun?.isEnrollable ? (
          <OCPromoBanner
            course={course}
            courseRun={course.activeCourseRun}
            data={promoBanner}
          />
        ) : null;
      },
    },
    {
      key: "whatYoullLearn",
      renderer: () => {
        return (
          <div className="[&_ul]:!px-0 md:[&_ul]:px-4">
            <WhatYoullLearn
              key="whatYoullLearn"
              title={tCourse("whatYoullLearn")}
              parseContent={course.productOverview}
            />
          </div>
        );
      },
    },
    {
      key: "skillLanguageOverview",
      renderer: () => {
        return course.associatedSkills?.length > 0 ||
          !syllabusHtml ||
          !isCurriculumInMainContent ? (
          <div className="flex flex-col gap-6 [&_h3]:!mb-4">
            <SkillLanguageOverview
              key="skillLanguageOverview"
              associatedSkills={course.associatedSkills}
              prerequisitesHtml={course.prerequisitesHtml}
              syllabusHtml={syllabusHtml}
              formattedLanguage={formattedLanguage}
              formattedTranscripts={formattedTranscripts}
              formattedAiLanguages={formattedAiLanguages}
              isCurriculumInMainContent={isCurriculumInMainContent}
            />
          </div>
        ) : null;
      },
    },
    {
      key: "emphasisModuleVersion",
      renderer: () => {
        return showCourseModes &&
          !isArchived &&
          course?.activeCourseRun?.isEnrollable &&
          emphasisModuleVersion &&
          emphasisModuleVersion !== RedesignedTrackComparison.Hidden ? (
          <div className={maxWidthContainer}>
            {emphasisModuleVersion === RedesignedTrackComparison.Static ? (
              <CertificateEmphasis
                key="certificateEmphasis"
                course={course}
                courseRun={course?.activeCourseRun}
                information={information}
                stats={stats}
                testimonial={testimonial}
                hidePrice={hidePrice}
              />
            ) : (
              <CertificateEmphasisTabs
                key="certificateEmphasisTabs"
                information={information}
                stats={stats}
                testimonial={testimonial}
                pathInfoCoursePrice={coursePrice}
                tabOrder={tabsOrder}
                hidePrice={hidePrice}
                pathInfo={pathInfo}
                course={course}
                ctaElementRenderer={(tabId: string, ctaClassName: string) => {
                  return (
                    <>
                      {coursePrice ? (
                        <CourseEnrollButton
                          buttonClass={ctaClassName}
                          courseRun={course.activeCourseRun!}
                          course={course}
                          location={`cert-module-${tabId}`}
                          price={coursePrice}
                        />
                      ) : (
                        <EnrollLink
                          buttonClass={ctaClassName}
                          courseRun={course.activeCourseRun!}
                          course={course}
                          location={`cert-module-${tabId}`}
                        />
                      )}
                      <AuditLink
                        courseRun={course.activeCourseRun!}
                        course={course}
                        className={ctaClassName}
                        location={`cert-module-${tabId}`}
                      />
                    </>
                  );
                }}
              />
            )}
          </div>
        ) : null;
      },
    },
    {
      key: "courseAbout",
      renderer: () => {
        return (
          <CourseAbout
            key="courseAbout"
            courseData={course}
            fullDescription={productFullDescription}
            video={courseHeaderVideo}
          />
        );
      },
    },
    {
      key: "careerCards",
      renderer: () => {
        const careerCardsData =
          CAREER_CARDS_VARIANTS[industryKey || "dataScience"];
        return (
          <CareerCards
            location="detail-page"
            isSideRail={careerCards?.isSideRail || false}
            industry={careerCards?.industry || industryKey || "dataScience"}
            variant={careerCards?.variantKey || "default"}
            heading={careerCards?.heading || ""}
            title={careerCards?.title || careerCardsData.heading}
            stats={(careerCards?.stats || careerCardsData.cards).slice()}
            skills={resolveCareerCardSkills(
              careerCards?.skills,
              course.skills,
              industryKey || "dataScience",
            ).slice()}
            hiddenCta={true}
          />
        );
      },
    },
    {
      key: "curriculum",
      renderer: () => {
        return syllabusHtml ? (
          <Curriculum
            key="curriculum"
            syllabus={syllabusHtml}
            weeksToComplete={weeksToComplete}
            minEffort={minEffort}
            maxEffort={maxEffort}
            formattedLanguage={formattedLanguage}
            formattedTranscripts={formattedTranscripts}
            formattedAiLanguages={formattedAiLanguages}
            prerequisitesHtml={course.prerequisitesHtml}
          />
        ) : null;
      },
    },
    {
      key: "courseAwards",
      renderer: () => {
        return course.customizations.catalogPage?.awards?.length ? (
          <CourseAwards
            key="courseAwards"
            awards={course.customizations.catalogPage.awards}
            isSpanish={false}
            width={172}
            height={172}
          />
        ) : null;
      },
    },
    {
      key: "benefitsModule",
      renderer: () => {
        return benefitsModule ? (
          <BenefitsModule
            location="detail-page"
            schoolName={titlePartner}
            benefitsModule={benefitsModule}
          />
        ) : null;
      },
    },
    {
      key: "midPageCta",
      renderer: () => {
        return (
          course.activeCourseRun && (
            <div className="[&_h2]:text-base md:[&_h2]:text-3xl py-4 md:py-0">
              <MidPageCTA
                key="midPageCta"
                course={course}
                courseRun={course.activeCourseRun}
                name="mid-page-cta"
                location="mid-page"
                buttonClass="flex-1"
                className="flex-1"
              />
            </div>
          )
        );
      },
    },
    {
      key: "fullProgramCta",
      renderer: () =>
        designToDisplay && ["1", "2", "3"].includes(designToDisplay) ? (
          <RelatedProgram
            designTobeSelected={designToDisplay}
            enrichedPrograms={enrichedPrograms || undefined}
            course={course}
            carouselData={carouselData}
            cardType={cardType}
            expandableSection={expandableSection}
          />
        ) : null,
    },
    {
      key: "learnerTestimonials",
      renderer: () => {
        return course.learnerTestimonials ? (
          <LearnerTestimonials
            key="learnerTestimonials"
            testimonials={course.learnerTestimonials}
          />
        ) : null;
      },
    },
    {
      key: "testimonialsModule", //redesign
      renderer: () => {
        return testimonialsModule ? (
          <SocialProof
            header={testimonialsModule.header.title}
            items={testimonialsModule.testimonials}
            linkText={testimonialsModule.header.ctaLinkText}
            linkUrl={testimonialsModule.header.ctaLinkUrl}
            eventConfig={courseDetailPageEventConfig}
            headerTextClassname={headerStylesInDetailPages}
          />
        ) : null;
      },
    },
    {
      key: "instructorList",
      renderer: () => {
        return course.activeCourseRun?.instructors &&
          course.activeCourseRun?.instructors.length > 0 ? (
          <InstructorList
            key="instructorList"
            instructors={course.activeCourseRun?.instructors}
          />
        ) : null;
      },
    },
    {
      key: "featuredInstructor",
      renderer: () => {
        return enrichedInstructors ? (
          <PartnerInstructors
            key="featuredInstructor"
            instructors={enrichedInstructors}
          />
        ) : null;
      },
    },
    {
      key: "faqs",
      renderer: () => {
        return <FAQs key="faqs" course={course} />;
      },
    },
    {
      key: "waysToTakeCourse",
      renderer: () => {
        return showCourseModes &&
          !isArchived &&
          course?.activeCourseRun?.isEnrollable ? (
          showRedesignedTrackComparison ? (
            <div className={maxWidthContainer}>
              <CertificateWaysToTakeCourse
                course={course}
                key="certificateWaysToTakeCourse"
                activeCourseRun={course.activeCourseRun}
                tableDescription={tableDescription}
                hidePrice={hidePrice}
              >
                <div className="flex flex-col md:flex-row gap-2 sm:gap-3 items-center">
                  {coursePrice ? (
                    <CourseEnrollButton
                      buttonClass="flex-1"
                      courseRun={course.activeCourseRun}
                      course={course}
                      location="cert-ways-to-take-course"
                      price={coursePrice}
                    />
                  ) : (
                    <EnrollLink
                      buttonClass="flex-1"
                      courseRun={course.activeCourseRun}
                      course={course}
                      location="cert-ways-to-take-course"
                    />
                  )}

                  <AuditLink
                    courseRun={course.activeCourseRun}
                    course={course}
                    className="flex-1"
                  />
                </div>
              </CertificateWaysToTakeCourse>
            </div>
          ) : (
            <div className={mainContentClass} key="waysToTakeCourse">
              <WaysToTakeCourse
                activeCourseRun={course.activeCourseRun}
                course={course}
              />
            </div>
          )
        ) : null;
      },
    },
    {
      key: "moreAboutCourse",
      renderer: () => {
        return course.additionalInformation ? (
          <MoreAboutCourse
            key="moreAboutCourse"
            additionalInformation={course.additionalInformation}
          />
        ) : null;
      },
    },
    {
      key: "socialShare",
      renderer: () => {
        return (
          <SocialShare
            key="socialShare"
            title={tCourse("shareThisCourse")}
            productUrl={course.productUrl}
            productName={course.productName}
          />
        );
      },
    },
    {
      key: "enterpriseCta",
      renderer: () => {
        return (
          <div
            className="bg-putty-300"
            key="enterpriseCta"
            style={{
              maxWidth: "none",
              width: "100vw",
              marginLeft: "calc(-50vw + 50%)",
            }}
          >
            {!course.frequentlyAskedQuestions && course.hasOfacRestrictions && (
              <OFACMessage type="course" />
            )}
            <EnterpriseCTA productName={courseTitle} variant="compact" />
          </div>
        );
      },
    },
    {
      key: "collapsedContent",
      renderer: () => {
        if (!collapsableContent) return null;
        return (
          <AccordionText>
            <AccordionTextItem
              trigger={
                collapsableContentLabel && collapsableContentLabel.length
                  ? collapsableContentLabel
                  : tCourse("learnMoreAboutThisCourse")
              }
            >
              <div className="flex flex-col gap-12 py-4 sm:gap-16 [&_h2]:text-xl sm:[&_h2]:text-2xl [&_h3]:text-xl sm:[&_h3]:text-2xl">
                {getSortedComponentsBySectionOrder({
                  componentList,
                  sectionsOrder: collapsableContent?.map(
                    (component) => component.component,
                  ),
                })}
              </div>
            </AccordionTextItem>
          </AccordionText>
        );
      },
    },
    {
      key: "relatedPrograms",
      renderer: () => {
        if (!relatedPrograms) return null;
        const partnersForProgramSearch = course.partners.map(
          (partner) => partner.name,
        );
        return (
          <PartnerProgramRecommendations
            key="relatedPrograms"
            location="single-course"
            title={relatedPrograms?.title}
            subtitle={relatedPrograms?.subtitle}
            cardsToDisplay={relatedPrograms?.cardsToDisplay}
            partners={partnersForProgramSearch}
            pageType="single-course"
          />
        );
      },
    },
    {
      key: "stepComponent",
      renderer: () => {
        return course.activeCourseRun ? (
          <StepComponent
            title={stepComponent?.title}
            steps={stepComponent?.steps}
            programIsEnrollable={course.activeCourseRun?.isEnrollable || false}
            promo={
              stepComponent?.promo
                ? {
                    ...stepComponent.promo,
                    ctaElement: (
                      <div className="flex flex-col md:flex-row gap-2 sm:gap-3 items-center">
                        {coursePrice ? (
                          <CourseEnrollButton
                            buttonClass="flex-1"
                            courseRun={course.activeCourseRun}
                            course={course}
                            location="step-component"
                            price={coursePrice}
                          />
                        ) : (
                          <EnrollLink
                            buttonClass="flex-1"
                            courseRun={course.activeCourseRun}
                            course={course}
                            location="step-component"
                          />
                        )}
                      </div>
                    ),
                  }
                : undefined
            }
            theme={stepComponent?.theme}
            key="contentSteps"
          />
        ) : null;
      },
    },
    {
      key: "countdownBanner",
      renderer: () => {
        return (
          <FeatureFlagComponentResolverClient
            flagKey="NEXT_PUBLIC_FEATURE_OC_COUNTDOWN_BANNER"
            variants={{
              test: (
                <CountdownBanner
                  variant="cta"
                  theme={countdownBanner?.btf?.theme || "light"}
                  header={countdownBanner?.btf?.text || ""}
                  subHeader={countdownBanner?.btf?.subtext || ""}
                  targetDate={countdownBanner?.btf?.date || ""}
                  htmlId="promo-countdown-banner"
                  location="single-course-detail-page"
                  name="promo-countdown-banner"
                  showSubheader={countdownBanner?.btf?.subtextEnabled ?? false}
                  showCTA={course.activeCourseRun?.isEnrollable ?? false}
                  ctaElement={
                    coursePrice ? (
                      <CourseEnrollButton
                        buttonClass="w-full"
                        courseRun={course.activeCourseRun!}
                        course={course}
                        location="countdown-banner"
                        price={coursePrice}
                      />
                    ) : (
                      <EnrollLink
                        buttonClass="w-full"
                        courseRun={course.activeCourseRun!}
                        course={course}
                        location="countdown-banner"
                      />
                    )
                  }
                  tooltip={countdownBanner?.btf?.tooltip}
                />
              ),
              legacy: <></>,
            }}
            fallback="legacy"
          />
        );
      },
    },
    {
      key: "bundleSavingsBanner",
      renderer: () => {
        return bundleSavingsBanner ? (
          <BundleSavingsBanner
            key="bundleSavingsBanner"
            title={bundleSavingsBanner?.title}
            titleEmphasis={bundleSavingsBanner?.titleEmphasis}
            badge={bundleSavingsBanner?.badge}
            description={bundleSavingsBanner?.description}
            discount={bundleSavingsBanner?.discount}
            bulletPoints={bundleSavingsBanner?.bulletPoints}
            cta={{
              label:
                bundleSavingsBanner?.cta?.label ||
                tProgram("exploreRelatedPrograms"),
              url: getProgramSearchUrl(course.partners),
              html_id: "bundling-cta",
              location: "single-course-detail-page",
              name: "single-course-bundling-CTA",
            }}
            partnerName={course.partners?.[0]?.name}
          />
        ) : null;
      },
    },
    {
      key: "productCompare",
      renderer: () => {
        const uuids = [
          course.productUuid,
          ...(productCompare?.uuids || compareFallbackUuids),
        ];

        return productCompare ? (
          <ProductCompare
            productCompare={productCompare}
            productUuids={uuids}
            currentCourseCheckoutUrl={compareCtaUrl}
            location="course-detail-page"
            currentCtaLabel={
              !isArchived && course.activeCourseRun?.isEnrollable
                ? (leadWithPaidData.value?.enrollCtaLabel ??
                  tCourse("advanceYourCareer"))
                : undefined
            }
          />
        ) : null;
      },
    },
  ];

  return (
    <>
      {course.activeCourseRun && (
        <ProductViewTracker
          productId={course.activeCourseRun.key}
          productType="course"
          productName={course.productName}
          productUrl={course.productUrl}
          courseId={course?.productUuid}
          checkoutUrl={checkoutUrl}
          metadata={{
            price: coursePrice || undefined,
            currency: "USD",
            partner: course.partners?.[0]?.key,
            category: course.subjectEn ?? course.subjectEs,
            enrollmentStart: course.activeCourseRun.enrollmentStart?.toString(),
            enrollmentEnd: course.activeCourseRun.enrollmentEnd?.toString(),
            courseRunEnd: course.activeCourseRun.end?.toString(),
          }}
        />
      )}

      <CourseTaggedHeader
        course={course}
        displayPrice={coursePrice}
        currency={pricingData?.currency}
        pricingData={pricingData}
        variant={headerBackground === "light" ? "light" : "dark"}
        paymentOption={paymentOption}
        showDynamicBadges={showDynamicBadges}
      />

      <CourseCurrencyBarSurface
        course={course}
        pricingData={pricingData}
        courseIsEnrollable={course.activeCourseRun?.isEnrollable ?? false}
        isArchived={isArchived}
      />

      {course.activeCourseRun && (
        <StickyCTA course={course} courseRun={course.activeCourseRun} />
      )}

      <div
        className="bg-putty-100 flex flex-col gap-12 sm:gap-16 pt-12"
        id="course-decided-details-body-container"
      >
        <div>
          <div className={mainContentClass}>
            {getSortedComponentsBySectionOrder({
              componentList,
              sectionsOrder: mainContent?.map(
                (component) => component.component,
              ),
            })}
          </div>
        </div>
      </div>
    </>
  );
}
